import { createContext, useContext, useMemo } from 'react';
import { api, call, type Schemas } from '../../api/client';
import { useApi } from '../../api/query';
import { applyMergePatch } from '../../realtime/mergePatch';
import { useTopic } from '../../realtime/hooks';

export type ShellStatus = Schemas['ShellStatus'];
export type Alert = Schemas['Alert'];
export type LicenceStatus = Schemas['LicenceStatus'];

/** Server block while nothing is known about the server yet. */
export const UNKNOWN_SERVER = {
  id: '',
  name: '—',
  status: 'unknown',
  connected: true,
  latency_ms: null,
  ticks_per_second: 0,
  last_tick_at: null,
  collector_location: '—',
  collector_status: 'online',
} as unknown as ShellStatus['server'];

/**
 * `GET /shell` merged with the live `shell` topic (server status, counts, server clock). When the REST
 * part fails, the topic alone still gives the server id and clock, which pages depend on; counts and the
 * licence banner are then left out, and callers already read them with fallbacks.
 */
export function useShellStatus(enabled: boolean) {
  const rest = useApi(['shell'], () => call(api.GET('/shell')), { enabled, refetchInterval: 30_000 });
  const live = useTopic<Partial<ShellStatus>>(enabled ? 'shell' : null);
  const data = useMemo(() => {
    if (rest.data) return applyMergePatch(rest.data, live.data || {}) as ShellStatus;
    if (!live.data) return undefined;
    // without GET /shell the topic still says which server this is
    const merged = applyMergePatch({ server: UNKNOWN_SERVER }, live.data) as ShellStatus;
    return merged.server.id ? { ...merged, server: { ...merged.server, name: merged.server.id } } : merged;
  }, [rest.data, live.data]);
  return { data, stale: live.stale, error: rest.error, refetch: rest.refetch };
}

/**
 * Latest alerts for the drawer and the toasts: the `alerts` topic snapshot (latest 20 and
 * counters), with `alert.created` / `alert.updated` events applied; `GET /alerts/feed` until the
 * snapshot arrives.
 */
export function useLiveAlerts(enabled: boolean) {
  const feed = useApi(['alerts', 'feed'], () => call(api.GET('/alerts/feed', { params: { query: { limit: 20 } } })), { enabled });
  const topic = useTopic<{ items: Alert[]; counters: Schemas['AlertCounters'] }>(enabled ? 'alerts' : null);
  return useMemo(() => {
    let items: Alert[] = (topic.data?.items || feed.data?.items || []).slice();
    for (const e of topic.events) {
      const a = e.data?.alert as Alert | undefined;
      if (!a) continue;
      const i = items.findIndex((x) => x.id === a.id);
      if (i >= 0) items[i] = { ...items[i], ...a };
      else items = [a, ...items];
    }
    return { items, counters: topic.data?.counters || feed.data?.counters, stale: topic.stale };
  }, [topic.data, topic.events, topic.stale, feed.data]);
}

/** Licence state (SHL-04): banners, read-only mode (actions disabled) and the locked screen. */
export interface LicenceState {
  status: LicenceStatus['status'] | 'unknown';
  readOnly: boolean;
  locked: boolean;
  banner: LicenceStatus['banner'];
  validUntil: string | null;
  graceEndsAt: string | null;
}
export const LicenceCtx = createContext<LicenceState>({
  status: 'unknown',
  readOnly: false,
  locked: false,
  banner: null,
  validUntil: null,
  graceEndsAt: null,
});
/** Pages read this to disable trading-server actions, alerts and scheduled reports in read-only mode. */
export const useLicence = () => useContext(LicenceCtx);

export function useLicenceStatus(enabled = true) {
  const q = useApi(['licence', 'status'], () => call(api.GET('/licence/status')), { enabled, refetchInterval: 300_000 });
  const d = q.data;
  return useMemo<LicenceState>(
    () => ({
      status: d?.status || 'unknown',
      readOnly: !!d?.read_only,
      locked: !!d?.locked,
      banner: d?.banner ?? null,
      validUntil: d?.valid_until ?? null,
      graceEndsAt: d?.grace_ends_at ?? null,
    }),
    [d],
  );
}
